import { Button, Flex, Stack, Text } from '@chakra-ui/react'
import { useApp, useMe } from '@hb/react'
import { colors, Log, LogEntry, UpdateCallback } from '@hb/shared'
import React, { useMemo, useState } from 'react'
import { useProfile } from '../../../../contexts/user/ProfileContext'
import { useSelectedPractice } from '../../../../hooks'
import { AssessmentGynNextAction, PatientGynNextAction } from '../NextAction'
import { ClaimNextActions } from './ClaimNextActions'
import { LogEntryModal, LogEntryView } from './LogEntry'
import { PlanNextActions } from './PlanNextActions'

export type LogViewType = 'assessment' | 'gyn'

export interface LogViewProps {
  maxHeight: number
  type: LogViewType
  log: Log | null
  updateLog: (id: string, newEntry: LogEntry) => Promise<UpdateCallback>
  canEdit?: boolean
}

const GynNextActions = () => {
  const { user } = useProfile()
  const { appName } = useApp()
  const { id: providerAppPracticeId } = useSelectedPractice()
  const practiceId = useMemo(() => {
    if (appName === 'providers-app') return providerAppPracticeId
    return (
      user?.gyn?.corrections?.practiceId ||
      user?.gyn?.data?.practiceId ||
      user?.midwives?.[0] ||
      null
    )
  }, [user, providerAppPracticeId, appName])
  const nextAction = useMemo(
    () => (appName === 'app' ? user?.adminGynNextAction : user?.practiceGynNextAction),
    [user?.adminGynNextAction, user?.practiceGynNextAction, appName],
  )
  return (
    <PatientGynNextAction
      patientId={user?.id}
      gynPracticeId={practiceId}
      inList={false}
      nextAction={nextAction}
    />
  )
}

const AssessmentNextActions = () => {
  const {
    selectedAssessment: { populated: selectedAssessment },
    assessmentId,
    claims: { data: claims },
  } = useProfile()
  const hasClaims = useMemo(() => Object.keys(claims ?? {}).length > 0, [claims])
  return (
    <>
      {selectedAssessment?.isGynProfile ? (
        <AssessmentGynNextAction
          inList={false}
          nextAction={selectedAssessment?.gynNextAction}
          assessmentId={assessmentId}
        />
      ) : null}
      {hasClaims ? <ClaimNextActions /> : null}
      <PlanNextActions />
    </>
  )
}

export const LogView: React.FC<LogViewProps> = ({
  maxHeight,
  type,
  log,
  updateLog,
  canEdit = true,
}) => {
  const me = useMe()
  const { appName } = useApp()
  const [selectedLogId, setSelectedLogId] = useState<number | undefined>(undefined)

  const sortedKeys = useMemo(
    () =>
      log
        ? Object.keys(log)
            .map(k => parseInt(k, 10))
            .sort((a, b) => b - a)
        : [],
    [log],
  )

  const logTitle = type === 'gyn' ? 'GYN Log' : 'Pregnancy Log'

  return (
    <Flex
      background="gray.50"
      flexGrow={1}
      overflow="hidden"
      width="100%"
      maxH={maxHeight}
      direction="column">
      {appName === 'app' || type === 'gyn' ? (
        <Flex width="100%" align="center" direction="column" borderBottom="1px solid #dedede">
          {type === 'gyn' ? <GynNextActions /> : <AssessmentNextActions />}
        </Flex>
      ) : null}
      <Flex direction="column" w="100%" flex={1} minH="0">
        <Flex
          bg={`${colors.green.hex}22`}
          px={3}
          borderBottom="1px solid #cdcdcd"
          align="center"
          w="100%">
          <Text py={1} fontFamily="Open Sans" color="gray.600" fontWeight={500}>
            {logTitle}
          </Text>
          {canEdit && appName === 'app' ? (
            <Flex ml="auto">
              <Button bg="gray.50" size="xs" variant="outline" onClick={() => setSelectedLogId(-1)}>
                + New Entry
              </Button>
              <LogEntryModal
                isOpen={selectedLogId === -1}
                onClose={() => setSelectedLogId(undefined)}
                placement="left"
                createdOn={selectedLogId}
                onSubmit={newEntry => updateLog(`${Date.now()}`, newEntry)}
              />
            </Flex>
          ) : null}
        </Flex>
        <Flex
          direction="column"
          overflowY="auto"
          flex={1}
          width="100%"
          minH="0"
          bg="white"
          w="100%">
          <Stack spacing={0} w="100%">
            {log ? (
              sortedKeys.map((e, i) => {
                const logEntry = log[e]
                if (!logEntry) return null
                return (
                  <LogEntryView
                    placement="left"
                    update={
                      logEntry.isInjected ||
                      logEntry.createdBy !== me?.uid ||
                      '' ||
                      logEntry.updatedBy !== me?.uid
                        ? undefined
                        : updated => updateLog(`${e}`, updated)
                    }
                    index={i}
                    key={e}
                    select={() => setSelectedLogId(e)}
                    deselect={() => setSelectedLogId(undefined)}
                    selected={selectedLogId === e}
                    {...logEntry}
                    createdOn={e}
                  />
                )
              })
            ) : (
              <Text p={2} pl={4} pr={4} color="#777">
                <i>No log entries yet</i>
              </Text>
            )}
          </Stack>
        </Flex>
      </Flex>
    </Flex>
  )
}
